import isEmpty from 'lodash/isEmpty';

import { ProviderType } from '../models/ProviderDetails';

export const formatProviderNameWithFirstMiddleLastName = (provider: any) =>
  [provider?.firstName, provider?.middleName, provider?.lastName]
    .filter((item) => !isEmpty(item) && item?.trim()?.length > 0)
    .map((item) => item.trim())
    .join(' ');

export const getNameAttrs = (provider) => ({
  firstName: provider?.firstName,
  middleName: provider?.middleName,
  lastName: provider?.lastName,
});

export const parseProviderName = (
  providerName: string,
  providerType: ProviderType,
  primaryDegree?: string,
  nameAttrs?
): string => {
  if (providerType === ProviderType.PRACTITIONER) {
    return `${formatProviderNameWithFirstMiddleLastName(nameAttrs)}${
      primaryDegree ? `, ${primaryDegree}` : ''
    }`;
  }

  if (providerName || providerType === ProviderType.MEDICAL_GROUP) {
    return providerName;
  }

  return '';
};

/**
 * @desc this function will captalize All first characters of words inside string
 * @param words String which needs to be capitalized
 * @returns Words
 */
export function capitalizeAllFirstCharacters(words) {
  return words?.replace(/(^\w|\s\w)/g, (w) => w.toUpperCase());
}

/**
 * @desc This function normalizes a medical group name by extracting ID, removing prefixes, decoding HTML entities, and formatting
 * @param input String which needs to be normalized. Supports formats like "123-name" or "Grp400009738-alma community network"
 * @returns Object containing extracted id and normalized value
 */
export function normalizeMedicalGroupName(input: string): {
  id: string | null;
  value: string;
} {
  if (!input || typeof input !== 'string') return { id: null, value: '' };

  let id: string | null = null;
  let result = input.trim();

  // Extract ID - matches both numeric (e.g., "123") and alphanumeric (e.g., "Grp400009738") prefixes
  const idMatch = result.match(/^([A-Za-z0-9]+)\s*-/);
  if (idMatch) {
    id = idMatch[1];
    result = result.replace(/^[A-Za-z0-9]+\s*-\s*/i, '');
  }

  // 2. Remove "Nonpar-" prefix
  result = result.replace(/^Nonpar-\s*/i, '');

  // Decode HTML entities
  result = result.replace(/&amp;/g, '&');

  // Normalize spacing
  result = result.replace(/\s+/g, ' ').trim();

  if (!result) return { id, value: '' };

  result = result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();

  return { id, value: result };
}

/**
 * @desc This function will capitalize first letter or string/word
 * @param words String which needs to be capitalized first character
 * @returns words
 */
export function capitalizeFirstLetters(words) {
  return words?.replaceAll(
    /\S*/g,
    (word) => `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`
  );
}

/**
 *
 * @param words String which needs to be make first words as a small character
 * @returns words
 */
export function capitalizeEachWord(words) {
  return words?.replaceAll(
    /[a-zA-Z]+(?![^(]*\))/g,
    (word) => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`
  );
}

/**
 *
 * @param words String which needs to be make first letter as a capital character with special char
 * @returns words
 */
export function capitalizeFirstLetterWithSpecialChar(words) {
  return words?.replaceAll(
    /\b\w*/g,
    (word) => `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`
  );
}

export const capitalizeFirstLetterOfEachWord = (
  textString?: string | null
): string => {
  if (textString !== undefined && textString != null && textString.length > 0) {
    return textString
      .split(' ')
      .map(
        (word) => word?.charAt(0)?.toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(' ');
  }
  return '';
};
